import React, { useEffect, useState } from "react";

import { DataTableBody } from "./components/DataTableBody/DataTableBody";
import { DataTableBulkActions } from "./components/DataTableBulkActions/DataTableBulkActions";
import { DataTableHeader } from "./components/DataTableHeader/DataTableHeader";
import { DataTablePagination } from "./components/DataTablePagination/DataTablePagination";
import { DataTableToolbar } from "./components/DataTableToolbar/DataTableToolbar";
import styles from "./DataTable.module.css";
import { getGridColumns, getPaginatedData,getPaginationInfo } from "./utils";

const DEFAULT_ROWS_OPTIONS = [10, 20, 50, 100];

export const DataTable = ({
  columns = [],
  data = [],
  getRowId = (row) => row?.id ?? row?.key,
  isLoading = false,
  isError = false,
  errorLabel,
  onRetry,
  emptyIllustration,
  emptyTitle = "No items found",
  emptyText = "There are no items to display.",
  loadingTitle = "Loading...",
  loadingText = "Please wait.",
  pagination,
  currentPage = 1,
  rowsPerPage = 20,
  totalCount,
  onPageChange,
  rowsOptions,
  rowsValue,
  onRowsChange,
  search,
  searchPlaceholder = "Search",
  sort,
  sortOptions = [],
  onSortChange,
  filters,
  filtersConfig = [],
  onFilterChange,
  selectable = false,
  /*
   * A table can hold rows the bulk actions cannot act on — e.g. "All Documents" lists documents
   * read from other endpoints beside the uploads, and emailing or deleting one of those is not a
   * thing the backend offers. Such a row gets no checkbox, and select-all skips it, rather than
   * offering a tick that leads to a failing bulk action.
   */
  isRowSelectable,
  selection = {},
  topActions = [],
  toolbarExtra,
  rowActions = [],
  bulkActions = [],
  onRowAction,
  getRowClassName,
  onRowClick,
  actionsColumnWidth,
  hideHeader = false,
  noScroll = false,
  getRowTestId,
  rowActionsLabel,
}) => {
  const isControlled = !!(onPageChange || pagination?.onPageChange);
  const [internalPage, setInternalPage] = useState(1);
  const [internalRows, setInternalRows] = useState(rowsPerPage);

  const effectiveRows = rowsValue ?? internalRows;
  const effectiveOnRowsChange = onRowsChange ?? ((val) => {
    setInternalRows(val);
    setInternalPage(1);
    if (isControlled) onPageChange?.(1);
  });

  const total = totalCount ?? data.length;
  const numericRows = effectiveRows === "All" ? (total || 9999) : effectiveRows;
  const page = isControlled
    ? (pagination?.page ?? currentPage)
    : internalPage;
  const { totalPages, start, end } = getPaginationInfo(
    total,
    page,
    numericRows
  );

  // When the data shrinks (e.g. switching type/filter while on a later page),
  // snap back to page 1 instead of getting stuck on an out-of-range empty page.
  useEffect(() => {
    if (!isControlled && totalPages > 0 && internalPage > totalPages) {
      setInternalPage(1);
    }
  }, [isControlled, totalPages, internalPage]);
  const paginatedData = getPaginatedData(
    data,
    page,
    numericRows,
    totalCount
  );

  const selectedCount = selection?.selectedCount ?? 0;
  const selectedItems = data.filter((row) =>
    selection?.isSelected?.(getRowId(row))
  );
  const toggleSelection = selection?.toggleSelection ?? (() => {});
  const isSelected = selection?.isSelected ?? (() => false);
  const selectAll = selection?.selectAll ?? (() => {});
  const deselectAll = selection?.deselectAll ?? (() => {});

  const resolvedActionsWidth = actionsColumnWidth ?? (() => {
    const primaryCount = rowActions.filter((a) => a.primary).length;
    const hasMenuActions = rowActions.some((a) => !a.primary);
    const buttonCount = primaryCount + (hasMenuActions ? 1 : 0);
    return `${buttonCount * 32 + Math.max(0, buttonCount - 1) * 4}px`;
  })();

  const gridColumns = getGridColumns(
    columns,
    selectable,
    rowActions?.length > 0,
    resolvedActionsWidth
  );

  const showToolbar =
    search ||
    toolbarExtra ||
    (filtersConfig?.length > 0) ||
    (sortOptions?.length > 0) ||
    topActions?.length > 0;
  const showBulkActions =
    selectable && selectedCount > 0 && bulkActions?.length > 0;

  // With no rows there is nothing to scroll to, so the placeholder states
  // (empty / error / loading) must not leave a horizontal scrollbar under an
  // otherwise blank table.
  const isShowingPlaceholder = isError || isLoading || paginatedData.length === 0;

  const selectableData = isRowSelectable
    ? data.filter((row) => isRowSelectable(row))
    : data;

  const isAllSelected =
    selectableData.length > 0 &&
    selectableData.every((r) => isSelected(getRowId(r)));
  const isIndeterminate =
    selectableData.some((r) => isSelected(getRowId(r))) && !isAllSelected;

  return (
    <div className={styles.wrapper}>
      {showToolbar && (
        <DataTableToolbar
          search={
            search && {
              value: search.value ?? search,
              onChange: search.onChange ?? search.onSearchChange,
              placeholder: search.placeholder ?? searchPlaceholder,
              maxWidth: search.maxWidth,
            }
          }
          filters={
            filters && {
              value: filters.value ?? filters,
              onChange: filters.onChange ?? onFilterChange,
            }
          }
          filtersConfig={filtersConfig}
          sort={sort}
          sortOptions={sortOptions}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          topActions={topActions}
          toolbarExtra={toolbarExtra}
        />
      )}
      {showBulkActions && (
        <DataTableBulkActions
          selectedCount={selectedCount}
          selectedItems={selectedItems}
          bulkActions={bulkActions}
          onDeselectAll={deselectAll}
          selection={selection}
        />
      )}
      <div
        className={`${styles.scrollContainer} ${isShowingPlaceholder ? styles.scrollContainerStatic : ""}`}
      >
      <div className={noScroll ? styles.tableInnerNoScroll : styles.tableInner}>
        {!hideHeader && (
          <DataTableHeader
            columns={columns}
            selectable={selectable}
            hasRowActions={rowActions?.length > 0}
            gridColumns={gridColumns}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            onSelectAll={() => selectAll(selectableData)}
            onDeselectAll={deselectAll}
          />
        )}
        <DataTableBody
          columns={columns}
          data={paginatedData}
          getRowId={getRowId}
          selectable={selectable}
          isRowSelectable={isRowSelectable}
          rowActions={rowActions}
          gridColumns={gridColumns}
          isSelected={isSelected}
          toggleSelection={toggleSelection}
          onRowAction={(action, r) => {
            onRowAction?.(action, r);
            action.onClick?.(r);
          }}
          isLoading={isLoading}
          isError={isError}
          errorLabel={errorLabel}
          onRetry={onRetry}
          emptyIllustration={emptyIllustration}
          emptyTitle={emptyTitle}
          emptyText={emptyText}
          loadingTitle={loadingTitle}
          loadingText={loadingText}
          getRowClassName={getRowClassName}
          onRowClick={onRowClick}
          getRowTestId={getRowTestId}
          rowActionsLabel={rowActionsLabel}
        />
      </div>
      </div>
      {!isLoading && !isError && total > 0 && (
        <DataTablePagination
          start={start}
          end={end}
          total={total}
          page={page}
          totalPages={totalPages}
          onPageChange={isControlled
            ? (onPageChange ?? pagination?.onPageChange)
            : (p) => setInternalPage(p)
          }
          rowsOptions={rowsOptions ?? DEFAULT_ROWS_OPTIONS}
          rowsPerPage={effectiveRows}
          onRowsChange={effectiveOnRowsChange}
        />
      )}
    </div>
  );
};
